/*
 * pingo-leve.js — by Studio Lab (Pingo build)
 *
 * Cart-drawer + cart-page "Leve também" carousel. Cards are rendered server-side
 * from `custom.leve_tambem_carrinho` (see snippets/pingo-leve.liquid). This file
 * only wires:
 *   - paged carousel (transform-based, one card per viewport; no 1/N counter)
 *   - sibling <select> → swaps image / title / href / variant dropdown / price
 *   - native-variant <select> → updates price + ADD variant id
 *   - ADD → /cart/add.js + CartLinesUpdateEvent. Morph resolves on add.js
 *     (sections HTML is already in that response). No extra /cart.js hop.
 *   - Strip `.adding` from lines whose variant was already in the cart
 *     (automatic discounts regenerate `item.key`; native would replay the
 *     entrance on every existing row).
 *
 * Loaded from snippets/scripts.liquid as type=module (always — there is no
 * on/off setting). The custom element is inert when the snippet self-gates.
 */

import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {{
 *   id: number,
 *   title: string,
 *   price: string,
 *   available: boolean,
 *   image: string | null,
 * }} LeveVariant
 *
 * @typedef {{
 *   id: number,
 *   title: string,
 *   label: string,
 *   url: string,
 *   image: string | null,
 *   imageAlt: string,
 *   variants: LeveVariant[],
 * }} LeveProduct
 *
 * @typedef {{
 *   defaultId: number,
 *   products: Record<string, LeveProduct>,
 * }} LevePack
 */

class PingoLeve extends Component {
  /** @type {number} */
  #currentIndex = 0;
  /** @type {boolean} */
  #wired = false;

  connectedCallback() {
    super.connectedCallback();
    this.#wireUI();
  }

  disconnectedCallback() {
    const card = this.querySelector('.pingo-leve__card');
    const gsap = /** @type {{ killTweensOf?: Function } | undefined} */ (window).gsap;
    if (card && typeof gsap?.killTweensOf === 'function') gsap.killTweensOf(card);
    super.disconnectedCallback();
  }

  #wireUI() {
    if (this.#wired) return;
    this.#wired = true;

    const cards = this.querySelectorAll('[data-card]');
    if (!cards.length) return;

    this.#wireCarousel(cards.length);
    cards.forEach((card) => this.#wireCard(/** @type {HTMLElement} */ (card)));
  }

  /**
   * @param {number} slideCount
   */
  #wireCarousel(slideCount) {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    const nav = /** @type {HTMLElement | null} */ (this.querySelector('[data-nav]'));

    if (!track) return;

    if (slideCount <= 1) {
      nav?.setAttribute('hidden', '');
      return;
    }

    nav?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    const updateUI = () => {
      track.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
      if (prev) prev.toggleAttribute('disabled', this.#currentIndex === 0);
      if (next) next.toggleAttribute('disabled', this.#currentIndex === slideCount - 1);
    };

    prev?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.#currentIndex > 0) {
        this.#currentIndex--;
        updateUI();
      }
    });

    next?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.#currentIndex < slideCount - 1) {
        this.#currentIndex++;
        updateUI();
      }
    });

    updateUI();
  }

  /**
   * @param {HTMLElement} card
   */
  #wireCard(card) {
    /** @type {LevePack | null} */
    let pack = null;
    const dataEl = card.querySelector('script[data-leve-pack]');
    if (dataEl?.textContent) {
      try {
        pack = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[pingo-leve] bad pack JSON', e);
      }
    }
    if (!pack?.products) return;

    const siblingSelect = /** @type {HTMLSelectElement | null} */ (card.querySelector('[data-sibling-select]'));
    const variantSelect = /** @type {HTMLSelectElement | null} */ (card.querySelector('[data-variant-select]'));
    const addBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-add]'));

    siblingSelect?.addEventListener('change', () => {
      this.#applyProduct(card, pack, siblingSelect.value);
    });

    variantSelect?.addEventListener('change', () => {
      const productId = siblingSelect?.value || String(pack.defaultId);
      const product = pack.products[productId];
      const variant = product?.variants.find((v) => String(v.id) === variantSelect.value);
      if (variant) this.#applyVariant(card, product, variant);
    });

    addBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled) return;
      this.#addToCart(variantId, addBtn);
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {LevePack} pack
   * @param {string} productId
   */
  #applyProduct(card, pack, productId) {
    const product = pack.products[productId];
    if (!product) return;

    const mediaLink = /** @type {HTMLAnchorElement | null} */ (card.querySelector('[data-media-link]'));
    const titleLink = /** @type {HTMLAnchorElement | null} */ (card.querySelector('[data-title-link]'));
    const img = /** @type {HTMLImageElement | null} */ (card.querySelector('.pingo-leve__img'));
    const variantWrap = /** @type {HTMLElement | null} */ (card.querySelector('[data-variant-wrap]'));
    const variantSelect = /** @type {HTMLSelectElement | null} */ (card.querySelector('[data-variant-select]'));

    if (mediaLink) mediaLink.href = product.url;
    if (titleLink) {
      titleLink.href = product.url;
      titleLink.textContent = product.title;
    }

    const variants = product.variants || [];
    if (variantSelect) {
      variantSelect.replaceChildren();
      for (const v of variants) {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title;
        opt.disabled = !v.available;
        variantSelect.append(opt);
      }
    }
    if (variantWrap) variantWrap.toggleAttribute('hidden', variants.length < 2);

    const current = variants.find((v) => v.available) || variants[0];
    if (!current) return;
    if (variantSelect) variantSelect.value = String(current.id);

    this.#applyVariant(card, product, current);

    if (img) {
      const src = current.image || product.image;
      if (src) {
        img.src = src;
        img.removeAttribute('srcset');
        img.alt = product.imageAlt || product.title;
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {LeveProduct} product
   * @param {LeveVariant} variant
   */
  #applyVariant(card, product, variant) {
    const priceEl = card.querySelector('[data-price]');
    const addBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-add]'));
    const img = /** @type {HTMLImageElement | null} */ (card.querySelector('.pingo-leve__img'));

    if (priceEl) priceEl.textContent = `+ ${variant.price}`;
    if (addBtn) {
      addBtn.dataset.variantId = String(variant.id);
      addBtn.disabled = !variant.available;
      addBtn.setAttribute('aria-label', `Adicionar ${product.title}`);
    }
    if (img && variant.image) {
      img.src = variant.image;
      img.removeAttribute('srcset');
    }
  }

  /**
   * Instant press feedback while /cart/add.js runs. The card itself stays put —
   * fading it out left a blank drawer until the morph (owner 2026-08-20).
   * @param {HTMLButtonElement} addBtn
   * @param {boolean} busy
   */
  #setBusy(addBtn, busy) {
    addBtn.classList.toggle('is-busy', busy);
    addBtn.toggleAttribute('aria-busy', busy);
    addBtn.disabled = busy;
    if (busy) {
      document.querySelectorAll('[ref="cartTotal"]').forEach((el) => el.setAttribute('shimmer', ''));
    }
  }

  /**
   * Cart-icon / live-region need a totalQuantity. add.js does not return the
   * full cart; the section HTML we already have does (`ref="cartItemCount"`).
   * Cheap regex — do not DOMParser the whole drawer before resolving morph.
   * @param {Record<string, string> | undefined} sections
   * @returns {number}
   */
  #itemCountFromSections(sections) {
    if (sections) {
      for (const html of Object.values(sections)) {
        if (typeof html !== 'string') continue;
        const match = html.match(/ref="cartItemCount"[^>]*>\s*(\d+)/);
        if (match) return Number(match[1]);
      }
    }
    const bubble = document.querySelector('[ref="cartBubbleCount"]');
    const current = Number.parseInt(bubble?.textContent || '0', 10);
    return Number.isFinite(current) ? current + 1 : 1;
  }

  /**
   * @param {string} variantId
   * @param {HTMLButtonElement} addBtn
   */
  #addToCart(variantId, addBtn) {
    if (addBtn.classList.contains('is-busy') || addBtn.disabled) return;

    this.#setBusy(addBtn, true);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    const payload = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    /** @type {{ routes?: { cart_add_url?: string } }} */
    const theme = /** @type {any} */ (window).Theme || {};
    const cartAddUrl = theme.routes?.cart_add_url || '/cart/add.js';

    const deferred = CartLinesUpdateEvent.createPromise();
    // Dispatched on `document`, not on this node. Native listeners live there
    // (cart-icon.js / cart-drawer.js) and CartLinesUpdateEvent exposes no
    // EventInit — bubbling is not a contract we can rely on. Same as pingo-gift.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          this.#setBusy(addBtn, false);
          return;
        }
        // Morph listens on this promise and only needs `detail.sections` — already
        // in the add.js body. A second /cart.js round-trip (currency for
        // createCartFromAjaxResponse) was the silent wait after the click.
        const itemCount = this.#itemCountFromSections(response.sections);
        deferred.resolve({
          cart: {
            id: 'pingo-leve',
            totalQuantity: itemCount,
            cost: { totalAmount: { amount: '0', currencyCode: 'BRL' } },
            lines: [],
            discountCodes: [],
          },
          detail: {
            items: response.items,
            source: 'pingo-leve',
            sourceId: this.id?.toString?.() || 'pingo-leve',
            itemCount,
            variantId,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((err) => {
        console.warn('[pingo-leve] add-to-cart failed:', err);
        deferred.reject(err);
        this.#setBusy(addBtn, false);
      });
  }
}

if (!customElements.get('pingo-leve')) {
  customElements.define('pingo-leve', PingoLeve);
}

/*
 * When an automatic discount (KIT JOGOS etc.) applies, Shopify regenerates
 * `item.key` (the hash after the colon). Native `#handleCartUpdate` then treats
 * every existing line as new and adds `.adding` to all of them — the card that
 * was already in the drawer collapses and grows with the new one. Identity is
 * the variant id (the key prefix). Native file untouched.
 */
(function pingoCartRowIdentity() {
  const variantOf = (key) => (key || '').split(':')[0];
  const rowList = () =>
    document.querySelectorAll('cart-items-component .cart-items__table-row[data-key]');

  let known = new Set([...rowList()].map((row) => variantOf(row.dataset.key)));

  const collectInserted = (mutations) => {
    const inserted = new Set();
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const rows = node.matches?.('.cart-items__table-row[data-key]')
          ? [node]
          : [...(node.querySelectorAll?.('.cart-items__table-row[data-key]') || [])];
        for (const row of rows) inserted.add(variantOf(row.dataset.key));
      }
    }
    return inserted;
  };

  const sync = (mutations) => {
    const inserted = collectInserted(mutations);

    for (const row of document.querySelectorAll(
      'cart-items-component .cart-items__table-row.adding'
    )) {
      const variant = variantOf(row.dataset.key);
      if (!variant || !known.has(variant)) continue;
      row.classList.remove('adding');
      row.getAnimations().forEach((animation) => animation.cancel());
    }

    const next = new Set();
    for (const row of rowList()) {
      const variant = variantOf(row.dataset.key);
      if (!variant || row.classList.contains('adding')) continue;
      if (inserted.has(variant) && !known.has(variant)) continue;
      next.add(variant);
    }
    known = next;
  };

  const roots = document.querySelectorAll('cart-items-component');
  if (!roots.length) return;
  const observer = new MutationObserver(sync);
  for (const root of roots) {
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }
})();

